import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowBack,
  Lock,
  Person,
  History
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { changePassword, getRecentActivity } from '../services/authService';
import { cambiarPasswordSchema } from '../validations/catalogValidation';

const UserProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [showPasswords, setShowPasswords] = useState({
    actual: false,
    nueva: false,
    confirmar: false
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(cambiarPasswordSchema),
    defaultValues: {
      passwordActual: '',
      passwordNueva: '',
      confirmPassword: ''
    }
  });

  // Cargar actividad reciente al montar el componente
  useEffect(() => {
    loadRecentActivity();
  }, []);

  const loadRecentActivity = async () => {
    try {
      setActivityLoading(true);
      const response = await getRecentActivity();
      setRecentActivity(response.data || []);
    } catch (error) {
      console.error('Error al cargar actividad:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await changePassword(data.passwordActual, data.passwordNueva);

      showSuccess('Contraseña actualizada exitosamente. Cerrando sesión...');
      reset();

      // Cerrar sesión automáticamente después del cambio (SEGURIDAD)
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);

    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error al cambiar la contraseña';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (tipo) => {
    const colors = {
      creacion: 'success',
      modificacion: 'info',
      eliminacion: 'error',
      deposito: 'primary',
      cambio_fecha: 'warning'
    };
    return colors[tipo] || 'default';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Volver
      </Button>

      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Mi Perfil
      </Typography>

      <Grid container spacing={3}>
        {/* Columna Izquierda */}
        <Grid item xs={12} md={6}>
          {/* Información del Usuario */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Información Personal</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Nombre</Typography>
                  <Typography variant="body1">{user?.nombre} {user?.apellido}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{user?.email}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Rol</Typography>
                  <Typography variant="body1">{user?.rol}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Empresa</Typography>
                  <Typography variant="body1">{user?.empresa?.nombre}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Formulario de Cambio de Contraseña */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Lock sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Cambiar Contraseña</Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Alert severity="warning" sx={{ mb: 3 }}>
                Al cambiar tu contraseña, tu sesión se cerrará automáticamente y deberás iniciar sesión nuevamente.
              </Alert>

              <Alert severity="info" sx={{ mb: 3 }}>
                La contraseña debe tener al menos 6 caracteres y ser diferente a la actual.
              </Alert>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  {/* Contraseña Actual */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Contraseña Actual"
                      type={showPasswords.actual ? 'text' : 'password'}
                      {...register('passwordActual')}
                      error={!!errors.passwordActual}
                      helperText={errors.passwordActual?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('actual')}
                              edge="end"
                            >
                              {showPasswords.actual ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  {/* Nueva Contraseña */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nueva Contraseña"
                      type={showPasswords.nueva ? 'text' : 'password'}
                      {...register('passwordNueva')}
                      error={!!errors.passwordNueva}
                      helperText={errors.passwordNueva?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('nueva')}
                              edge="end"
                            >
                              {showPasswords.nueva ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  {/* Confirmar Contraseña */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirmar Nueva Contraseña"
                      type={showPasswords.confirmar ? 'text' : 'password'}
                      {...register('confirmPassword')}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('confirmar')}
                              edge="end"
                            >
                              {showPasswords.confirmar ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  {/* Botones */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => reset()}
                        disabled={loading}
                      >
                        Limpiar
                      </Button>
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <Lock />}
                      >
                        {loading ? 'Actualizando...' : 'Cambiar Contraseña'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Columna Derecha - Actividad Reciente */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <History sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Actividad Reciente</Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {activityLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : recentActivity.length === 0 ? (
                <Alert severity="info">No hay actividad registrada</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Acción</TableCell>
                        <TableCell>Tabla</TableCell>
                        <TableCell>Fecha</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentActivity.map((activity) => (
                        <TableRow key={activity.id}>
                          <TableCell>
                            <Chip
                              label={activity.tipo_accion}
                              size="small"
                              color={getActionColor(activity.tipo_accion)}
                            />
                          </TableCell>
                          <TableCell>{activity.tabla_afectada}</TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {dayjs(activity.fecha_hora).format('DD/MM/YYYY HH:mm')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default UserProfilePage;
